/**
 * Shared annotated-image export for parsed P&ID / PFD diagrams (FS-622).
 *
 * The colored bounding boxes / line overlays a user sees in the editor are NOT
 * baked into any server-side image — `properties.image` is just the clean,
 * processed background. The boxes are rendered client-side from the parsed
 * graph. This module extracts that render so both the editor's "Download"
 * button and the Data Catalog row download produce the identical annotated PNG.
 */
import { NODE_TYPE_COLORS } from '@/constants/graph'
import { apiService } from '@/service/api'
import { parseGraph, type Node, type SymbolNode, type ConnectionNode } from '@/service/graph'
import type { Rectangle } from '@/routes/GraphEditor/PnidRenderer'
import { drawWktOnCanvas, stripFileExtension } from '@/utils/graphExport'
import { resolveGraphOpen } from '@/utils/resolveGraphOpen'

/**
 * Build the overlay rectangles from parsed graph nodes — the non-interactive
 * subset of EditorWrapper's rectangle builder (no highlight / search / needs-
 * type recoloring) so a downloaded image matches the editor's default view.
 */
export function buildExportRectangles(nodes: Node[]): Rectangle[] {
  const rectangles: Rectangle[] = []
  for (const node of nodes) {
    if (node.type === 'symbol' || node.type === 'tag') {
      // Skip nodes without bounding-box data (only visible in graph preview).
      if (
        node.x === undefined ||
        node.y === undefined ||
        node.width === undefined ||
        node.height === undefined
      ) {
        continue
      }
      const color =
        node.type === 'symbol'
          ? (node as SymbolNode).is_instrument
            ? NODE_TYPE_COLORS.instrument_symbol
            : NODE_TYPE_COLORS.symbol
          : NODE_TYPE_COLORS.tag
      rectangles.push({
        id: node.id,
        x: node.x,
        y: node.y,
        width: node.width,
        height: node.height,
        color,
        zIndex: 0,
        vertices: node.vertices,
      })
    } else if (node.type === 'connection') {
      const connection = node as ConnectionNode
      if (connection.geometry_wkt?.includes('LINESTRING')) {
        rectangles.push({
          id: node.id,
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          color: NODE_TYPE_COLORS.connection,
          zIndex: 0,
          geometry_wkt: connection.geometry_wkt,
        })
      }
    }
  }
  return rectangles
}

/**
 * Failure codes rejected by renderAnnotatedPnidBlob. Exported so callers map
 * them to UI without matching hard-coded strings (compile-safe single source).
 */
export const PNID_RENDER_ERROR = {
  canvasContext: 'canvas_context_failed',
  createImage: 'create_image_failed',
  imageLoad: 'pnid_load_failed',
} as const

/**
 * Render the processed P&ID background (loaded from `imageUrl`) with the parsed
 * bounding boxes / line overlays drawn on top, resolving a PNG blob. Mirrors
 * the editor's canvas export so both entry points yield the same image.
 */
export function renderAnnotatedPnidBlob(imageUrl: string, rectangles: Rectangle[]): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight

      const ctx = canvas.getContext('2d', { alpha: false })
      if (!ctx) {
        reject(new Error(PNID_RENDER_ERROR.canvasContext))
        return
      }

      // Draw the background P&ID image at its natural size.
      ctx.drawImage(img, 0, 0)

      // Scale line thickness relative to image resolution so it looks consistent.
      ctx.lineWidth = Math.max(2, Math.round(img.naturalWidth / 800))
      for (const rect of rectangles) {
        ctx.strokeStyle = rect.color
        if (rect.vertices && rect.vertices.length >= 3) {
          // Freeform polygon outline (ENG-2582) — matches PnidRenderer's
          // <polygon> so traced shapes export as polygons, not bounding boxes.
          ctx.beginPath()
          ctx.moveTo(rect.vertices[0].x, rect.vertices[0].y)
          for (let i = 1; i < rect.vertices.length; i++) {
            ctx.lineTo(rect.vertices[i].x, rect.vertices[i].y)
          }
          ctx.closePath()
          ctx.stroke()
        } else if (rect.geometry_wkt?.includes('LINESTRING')) {
          drawWktOnCanvas(ctx, rect.geometry_wkt)
        } else {
          ctx.strokeRect(rect.x, rect.y, rect.width, rect.height)
        }
      }

      // quality (3rd arg) is ignored for image/png — omit it.
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error(PNID_RENDER_ERROR.createImage))
          return
        }
        resolve(blob)
      }, 'image/png')
    }
    img.onerror = () => reject(new Error(PNID_RENDER_ERROR.imageLoad))
    img.src = imageUrl
  })
}

/**
 * Produce the annotated P&ID/PFD image (processed background + parsed boxes)
 * for a catalog document, matching what the editor shows. Returns null when the
 * document has no resolvable graph yet (still processing) so callers can fall
 * back to the original file. Multi-page sets render their first sheet, which is
 * the page the editor opens on. FS-622.
 */
export async function exportAnnotatedPnidForDocument(
  doc: { id: string; name: string; properties?: { graph_id?: string; pnid_graph_ids?: unknown } | null }
): Promise<{ blob: Blob; fileName: string } | null> {
  // Both graphId and firstPage come from the same sorted traversal so the graph
  // and the requested image page can never drift apart.
  const { graphId, firstPage } = resolveGraphOpen(doc.properties)
  if (!graphId) return null

  const imageOptions =
    firstPage != null ? { type: 'processed' as const, page: firstPage } : { type: 'processed' as const }

  const [imageBlob, graphData] = await Promise.all([
    apiService.downloadPnidImage(doc.id, imageOptions),
    apiService.getGraphData(graphId),
  ])

  const { nodes } = parseGraph(graphData)
  const rectangles = buildExportRectangles(nodes)

  const imageUrl = URL.createObjectURL(imageBlob)
  try {
    const blob = await renderAnnotatedPnidBlob(imageUrl, rectangles)
    return { blob, fileName: `${stripFileExtension(doc.name)}.png` }
  } finally {
    URL.revokeObjectURL(imageUrl)
  }
}
